// 納品書リソースの宣言。既存の delivery.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
//
// 【業務フロー】納品書は見積からの継承作成のみ（ユーザー指示:「納品日さえ入れれば納品継承」）。
// 明細・金額・顧客は createDeliveryFromQuote が見積から丸ごと継承するため、
// AI が渡すのは「どの見積か（quoteCode）」と「納品日（deliveryDate）」だけ。
import { z } from 'zod';
import {
  fetchDeliveries,
  createDeliveryFromQuote,
  updateDelivery,
  type DeliveryUpdateInput,
} from '@modules/delivery/services/delivery.service';
import { fetchQuotes } from '@modules/quote/services/quote.service';
import type { Delivery, ID } from '@shared/types';
import type { ListFilters, ResourceDescriptor, WriteResult } from './types';
import {
  MAX_ROWS,
  aiDocItemSchema,
  aiStampFlagsSchema,
  calcDocTotals,
  dateString,
  normalizeCode,
  resolveEmployee,
  resolveUsergroup,
  toDateString,
  toDocLineItems,
  toTimestamp,
} from './shared';

/** 納品書1件を、LLM が読む最小限の形へ射影する。 */
function toCompactDelivery(d: Delivery) {
  return {
    code: d.code,
    title: d.title,
    customer: d.partnerSnapshot?.name ?? null,
    total: d.grandtotal, // 税込合計
    deliveryDate: toDateString(d.deliveryDate),
    createdAt: toDateString(d.createdAt),
  };
}

async function list(filters: ListFilters): Promise<unknown> {
  const deliveries = await fetchDeliveries();

  const keyword = filters.keyword;
  const filtered = keyword
    ? deliveries.filter(
        (d) =>
          d.title.includes(keyword) ||
          d.code.includes(keyword) ||
          (d.partnerSnapshot?.name ?? '').includes(keyword)
      )
    : deliveries;

  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    deliveries: filtered.slice(0, MAX_ROWS).map(toCompactDelivery),
  };
}

async function get(rawCode: string): Promise<unknown> {
  const code = normalizeCode(rawCode);
  const deliveries = await fetchDeliveries();
  const found = deliveries.find((d) => normalizeCode(d.code) === code);
  if (!found) {
    return { found: false, note: `納品書番号「${rawCode}」は見つかりませんでした` };
  }
  return { found: true, delivery: toCompactDelivery(found) };
}

// ── 書込（C-2）─────────────────────────────────────────────────

const writeCreateSchema = z.object({
  quoteCode: z.string().min(1, 'quoteCode（見積番号）は必須'),
  deliveryDate: dateString,
});

const writeUpdateSchema = z
  .object({
    title: z.string().optional(),
    deliveryDate: dateString.optional(),
    issueDate: dateString.optional(),
    printNote: z.string().optional(),
    internalNote: z.string().optional(),
    assignee: z.string().min(1).optional(),
    chiefEmployer: z.string().min(1).optional(),
    usergroup: z.string().min(1).optional(),
    stampFlags: aiStampFlagsSchema.optional(),
    /** 渡すと明細を「全て置き換える」（追記ではない）。金額は自動再計算される。 */
    items: z.array(aiDocItemSchema).min(1).max(30).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

/** code から更新対象の納品書を引き当てる（get と同じ正規化・完全一致のみ）。 */
async function findByCode(rawCode: string): Promise<Delivery | null> {
  const code = normalizeCode(rawCode);
  const deliveries = await fetchDeliveries();
  return deliveries.find((d) => normalizeCode(d.code) === code) ?? null;
}

export const deliveryResource: ResourceDescriptor = {
  name: 'delivery',
  label: '納品書',
  list,
  get,
  write: {
    fields: [
      { name: 'quoteCode', label: '見積番号', type: 'ref', resource: 'quote', required: true, note: '作成時のみ・継承元の見積番号（例 0001-01）' },
      { name: 'deliveryDate', label: '納品日', type: 'date', required: true, note: 'YYYY-MM-DD（作成時必須・更新時任意）' },
      { name: 'title', label: '件名', type: 'string', note: '更新のみ' },
      { name: 'issueDate', label: '発行日', type: 'date', note: '更新のみ。YYYY-MM-DD' },
      { name: 'printNote', label: '備考（帳票印字）', type: 'string', note: '更新のみ' },
      { name: 'internalNote', label: '社内メモ', type: 'string', note: '更新のみ' },
      { name: 'assignee', label: '担当者', type: 'string', note: '更新のみ。担当者名で指定（IDは不要）' },
      { name: 'chiefEmployer', label: '確認者', type: 'string', note: '更新のみ。確認者の社員名で指定（IDは不要）' },
      { name: 'usergroup', label: '担当部署', type: 'string', note: '更新のみ。部署名で指定（IDは不要）' },
      { name: 'stampFlags', label: '印鑑', type: 'string', note: '更新のみ。{tantou担当印, kakunin確認印, kaisha会社印} を true/false で3つとも指定' },
      { name: 'items', label: '明細', type: 'items', note: '更新のみ。配列 [{name,quantity,unit?,unitPrice}]。全置き換え・金額は自動再計算' },
    ],
    createSchema: writeCreateSchema,
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      const input = data as z.infer<typeof writeCreateSchema>;

      // ① 継承元の見積を解決する（表記ゆれは normalizeCode で吸収）
      const code = normalizeCode(input.quoteCode);
      const quotes = await fetchQuotes();
      const quote = quotes.find((q) => normalizeCode(q.code) === code);
      if (!quote) {
        throw new Error(`見積「${input.quoteCode}」が見つかりません`);
      }
      // 帳票番号は案件番号-枝番の体系。案件に紐づかない見積は納品書の枝番を採番できない
      if (!quote.orderId) {
        throw new Error('案件に紐づく見積のみ納品書へ継承できます');
      }

      // ② 継承作成（明細・金額・顧客・重複チェック・案件進行は本体サービスの責務）。
      //   納品日とレビュー待ちの印は overrides で「1回の作成」に原子的に含める
      //   （後追い更新だと、失敗時にタグ無しのAIレコードが残るため）
      const created = await createDeliveryFromQuote(quote.id, quote.orderId, userId, {
        deliveryDate: toTimestamp(input.deliveryDate),
        aiPendingReview: true,
      });
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const target = await findByCode(code);
      if (!target) return null;
      const input = changes as z.infer<typeof writeUpdateSchema>;

      const patch: DeliveryUpdateInput = { aiPendingReview: true };
      if (input.title !== undefined) patch.title = input.title;
      if (input.deliveryDate !== undefined) patch.deliveryDate = toTimestamp(input.deliveryDate);
      if (input.issueDate !== undefined) patch.issueDate = toTimestamp(input.issueDate);
      if (input.printNote !== undefined) patch.printNote = input.printNote;
      if (input.internalNote !== undefined) patch.internalNote = input.internalNote;
      // 担当者・確認者・部署は名前→ID解決（IDをLLMに組ませない）
      if (input.assignee !== undefined) patch.assignedUserId = (await resolveEmployee(input.assignee)).id;
      if (input.chiefEmployer !== undefined) patch.chiefEmployerId = (await resolveEmployee(input.chiefEmployer)).id;
      if (input.usergroup !== undefined) patch.usergroupId = (await resolveUsergroup(input.usergroup)).id;
      if (input.stampFlags !== undefined) patch.stampFlags = input.stampFlags;

      // 明細は「全置き換え」。金額（subtotal/totalgst/grandtotal）は明細から再計算する
      // （grandtotal = subtotal + advance + totalgst は upsertQuoteItems と同じ式）
      if (input.items && input.items.length > 0) {
        const taxCalcMode = target.taxCalcMode ?? 'floor';
        const totals = calcDocTotals(input.items, taxCalcMode);
        patch.lineItems = toDocLineItems(input.items);
        patch.subtotal = totals.subtotal;
        patch.totalgst = totals.taxAmount;
        patch.grandtotal = totals.subtotal + (target.advance ?? 0) + totals.taxAmount;
      }

      await updateDelivery(target.id, patch, userId);
      return { id: target.id, code: target.code };
    },
    link: (id) => `/deliveries/${id}/edit`,
  },
};
